<template>
  <div v-if="hiringProcess && Object.keys(hiringProcess).length > 0">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <SectionHeader :title="`${companyName} Hiring Process: Application Steps, Interview Questions & Timeline`"
        class="mb-6" />
      <div class="flex">
        <LastUpdatedDate section="hiring-process" />
      </div>
    </div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
      <HiringProcessOverview v-if="hiringProcess.overview" :overview="hiringProcess.overview"
        :company-name="companyName" />
      <HiringProcessQuickFacts v-if="hiringProcess.quick_facts && Object.keys(hiringProcess.quick_facts).length > 0"
        :quick-facts="hiringProcess.quick_facts" :brand-color="currentBrandColor" />
      <HiringProcessSteps v-if="hiringProcess.hiring_steps && Object.keys(hiringProcess.hiring_steps).length > 0"
        :hiring-steps="hiringProcess.hiring_steps" :brand-color="currentBrandColor" :company-name="companyName" />
      <HiringProcessInterviewQuestions
        v-if="hiringProcess.interview_questions && hiringProcess.interview_questions.length > 0"
        :interview-questions="hiringProcess.interview_questions" :brand-color="currentBrandColor"
        :company-name="companyName" />
      <div v-if="hiringProcess.faq && Object.keys(hiringProcess.faq).length > 0">
        <SectionHeader title="Frequently Asked Questions" class="mb-6 mt-8" />
        <div class="bg-white rounded-lg my-4">
          <div class="space-y-4">
            <FaqQuestion v-for="(answers, question) in hiringProcess.faq" :key="question"
              :question="question.toString()" :answers="answers" :brand-color="currentBrandColor" />
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
      <SectionHeader :title="`Latest Job Openings at ${companyName}`" class="mb-6">
        <template #action>
          <NuxtLink :to="`/company/${permalink}/jobs`" class="hidden sm:block">
            <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="currentBrandColor" />
          </NuxtLink>
        </template>
      </SectionHeader>
      <div class="block sm:hidden mb-4">
        <NuxtLink :to="`/company/${permalink}/jobs`">
          <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
            class="justify-start" :brand-color="currentBrandColor" />
        </NuxtLink>
      </div>
      <CompanyTopJobs :permalink="permalink" :location="''" :company_name="companyName"
        :pre-fetched-data="preFetchedJobsData" :is-loading="jobsDataPending" :logo_url="logoUrl" />
    </div>
  </div>
  <div v-else class="rounded-lg border border-slate-200 bg-white p-12 text-center">
    <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
      <svg class="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <h3 class="mb-2 text-lg font-semibold text-slate-900">
      No Hiring Process Information Available
    </h3>
    <p class="text-slate-600">
      We don't have detailed hiring process information for {{ companyName }} yet. Check back soon
      for updates.
    </p>
  </div>
</template>
<script setup lang="ts">
import SectionHeader from '../common/SectionHeader.vue'
import ActionButton from '../common/ActionButton.vue'
import LastUpdatedDate from './LastUpdatedDate.vue'
import FaqQuestion from './FaqQuestion.vue'
import HiringProcessOverview from './HiringProcessOverview.vue'
import HiringProcessQuickFacts from './HiringProcessQuickFacts.vue'
import HiringProcessSteps from './HiringProcessSteps.vue'
import HiringProcessInterviewQuestions from './HiringProcessInterviewQuestions.vue'
import CompanyTopJobs from './CompanyTopJobs.vue'
interface Props {
  permalink: string
  brandColor?: string
  hiringProcess: any
  companyName: string
  jobsData?: any
  logoUrl?: string
  preFetchedData?: any
}
const props = withDefaults(defineProps<Props>(), {
  brandColor: '#5F0CE2',
  jobsData: null,
  logoUrl: '',
  preFetchedData: null
})
const currentBrandColor = computed(() => props.brandColor)
const preFetchedJobsData = computed(() => {
  return props.preFetchedData?.jobsData || { jobs: [], total: 0, available: 0 }
})
const jobsDataPending = computed(() => {
  return !props.preFetchedData?.jobsData
})
</script>